/**
 * Google Ads click-id capture.
 *
 * The client-side "Free Scan Initiated" conversion is best-effort by nature —
 * an ad blocker, a still-loading gtag.js, or a flaky network all swallow it
 * silently. The server can re-upload that conversion, but only for traffic it
 * can attribute to an ad click, and the only thing carrying that attribution
 * is the click id Google appends to the landing URL.
 *
 * It appears exactly once, on arrival, and the visitor may browse several
 * pages before submitting a scan, so it is stashed in a first-party cookie
 * scoped to the registrable parent — the marketing site captures it, the
 * dashboard's own scan form can read it.
 *
 * This cookie exists to feed Google Ads and is gated accordingly: GPC wins
 * outright, an explicit opt-out closes it, and the gate is re-checked on read
 * as well as on write so a mid-session opt-out takes effect immediately
 * instead of waiting for the cookie to expire.
 */

import { setCookie, readCookie, clearCookie } from './cookies';
import { hasConsent } from './tracking';

/** First-party cookie holding `"<type>:<value>"`. */
export const CLICK_ID_COOKIE = 'privisy_click_id';

/**
 * 90 days, matching the click-through conversion window on the Ads action.
 * A click id older than the window cannot produce an attributed conversion,
 * so keeping it past that point would only widen the retention for nothing.
 */
const CLICK_ID_TTL_SECONDS = 90 * 24 * 60 * 60;

/**
 * In precedence order. `gclid` is the ordinary search/display click id;
 * `gbraid` and `wbraid` are what Google substitutes for iOS and app campaigns
 * where a gclid cannot be issued. Google sends one, but a stitched-together
 * URL can carry several — prefer the most attributable.
 */
export const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;

export type ClickIdType = (typeof CLICK_ID_PARAMS)[number];

export interface ClickId {
  type: ClickIdType;
  value: string;
}

/**
 * Click ids are URL-safe base64-ish and comfortably under 512 chars. The value
 * is echoed back to the Ads API and stored in our own database, so anything
 * outside that shape is rejected at the door rather than carried around.
 */
const CLICK_ID_VALUE = /^[A-Za-z0-9_-]{1,512}$/;

/** Extract a click id from a query string (`location.search`), or null. */
export function parseClickId(search: string): ClickId | null {
  if (!search) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  for (const type of CLICK_ID_PARAMS) {
    const value = params.get(type);
    if (value && CLICK_ID_VALUE.test(value)) return { type, value };
  }
  return null;
}

/**
 * Capture the click id from the current URL, if there is one and the visitor
 * has not opted out. A page load without a click id leaves any previously
 * captured one alone — the visitor is still on the same ad-sourced journey,
 * they have just navigated. A page load *with* one overwrites, so the most
 * recent click gets the credit.
 *
 * Also clears a stored click id when consent has since been withdrawn, so the
 * opt-out is honored in storage and not just at read time.
 */
export function captureClickId(): void {
  if (typeof window === 'undefined') return;
  if (!hasConsent()) {
    clearCookie(CLICK_ID_COOKIE);
    return;
  }
  const clickId = parseClickId(window.location.search);
  if (!clickId) return;
  setCookie(CLICK_ID_COOKIE, `${clickId.type}:${clickId.value}`, CLICK_ID_TTL_SECONDS);
}

/** The captured click id, or null when there is none or consent is absent. */
export function readClickId(): ClickId | null {
  if (!hasConsent()) return null;
  const raw = readCookie(CLICK_ID_COOKIE);
  if (!raw) return null;
  const separator = raw.indexOf(':');
  if (separator < 0) return null;
  const type = raw.slice(0, separator);
  const value = raw.slice(separator + 1);
  if (!(CLICK_ID_PARAMS as readonly string[]).includes(type)) return null;
  if (!CLICK_ID_VALUE.test(value)) return null;
  return { type: type as ClickIdType, value };
}
